import { useEffect, useState } from 'react';
import { useUnit } from 'effector-react';
import dayjs from 'dayjs';
import clsx from 'clsx';
import { $initialCurrenciesAvailableStore, $monthlyTransactionStore } from '@lib/store';
import { DateUtils } from '@shared/utils';
import type { PaymentSummariesResponse } from '@shared/api/rest/dashboards';
import { TransactionItem } from './transactionItem';
import { RequestTransaction } from './requestTransaction';
import { Select, Spinner, Text } from '@shared/widgets';
import { ItemType } from '@shared/api/models';

export interface ITransactionProps {
  minimalYear: number;
  minimalMonth: number;
}

const navigation = [
  { id: 1, name: 'Outgoing Transactions' },
  { id: 2, name: 'Incoming Transactions' },
  { id: 3, name: 'Request Outgoing Transactions Summary' },
];

const currentMonth = DateUtils.getCurrentMonth();
const currentMonthIndex = dayjs().month() + 1;
const currentYear = dayjs().year();

export const MonthlyTransaction = (props: ITransactionProps) => {
  const currenciesAvailableStore = useUnit($initialCurrenciesAvailableStore.store);
  const payment = useUnit($monthlyTransactionStore.store);

  const yearsList = DateUtils.getYearsList(props.minimalYear);
  const [selectedYear, setSelectedYear] = useState<string | undefined>(yearsList[yearsList.length - 1]);
  const monthsList = DateUtils.getMonthsSelectList(
    +selectedYear! === props.minimalYear ? props.minimalMonth : 1,
    +selectedYear! === currentYear ? currentMonthIndex : 12,
  );
  const [selectedMonth, setSelectedMonth] = useState<string | undefined>(monthsList.find((month) => month.label === currentMonth)?.value);
  const [currency, setCurrency] = useState<string>(currenciesAvailableStore.data[0]);
  const [activeNavItem, setActiveNavItem] = useState<number>(1);

  useEffect(() => {
    if (currency) {
      $monthlyTransactionStore.events.clear();
      $monthlyTransactionStore.events.update({
        Currency: currency,
        DateFrom: DateUtils.getFirstDateOfYear(selectedYear, selectedMonth),
        DateTo: DateUtils.getLastDateOfYear(selectedYear, selectedMonth),
      });
    }
  }, [currency, selectedYear, selectedMonth]);

  const getTransactionItem = () => {
    if (payment.loading) return <Spinner />;

    switch (activeNavItem) {
      case 1:
        return (
          <TransactionItem
            {...(payment.data as PaymentSummariesResponse).outgoingTransactions}
            textValue={`${monthsList.find((month) => month.value === selectedMonth)?.label} ${selectedYear!}`}
            currency={currency!}
            itemType={ItemType.Outgoing}
          />
        );
      case 2:
        return (
          <TransactionItem
            {...(payment.data as PaymentSummariesResponse).incomingTransactions}
            textValue={`${monthsList.find((month) => month.value === selectedMonth)?.label} ${selectedYear!}`}
            currency={currency!}
            itemType={ItemType.Incoming}
          />
        );
      case 3:
        return (
          <RequestTransaction
            {...(payment.data as PaymentSummariesResponse).outgoingTransactionSummary}
            textValue={`${monthsList.find((month) => month.value === selectedMonth)?.label} ${selectedYear!}`}
            currency={currency!}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div>
      <div className='flex justify-between items-center'>
        <Text className='mr-16' size='2xl'>
          Monthly Transaction Summary
        </Text>
        <div className='flex gap-6'>
          <Select className='min-w-[100px]' items={currenciesAvailableStore.data} value={currency} onChange={(value) => setCurrency(value!)} />
          <Select items={monthsList} value={selectedMonth} onChange={setSelectedMonth} />
          <Select items={yearsList} value={selectedYear} onChange={setSelectedYear} />
        </div>
      </div>
      <div className='flex my-6'>
        {navigation.map((block) => (
          <div
            key={block.id}
            className={clsx(
              'p-4 text-slate dark:text-white cursor-pointer',
              activeNavItem === block.id ? 'bg-white dark:bg-soft-black font-semibold text-soft-black dark:text-white' : 'dark:opacity-60',
            )}
            onClick={() => setActiveNavItem(block.id)}
          >
            {block.name}
          </div>
        ))}
      </div>
      {getTransactionItem()}
    </div>
  );
};
